// apps/client/src/pages/leads/edm/components/BlockEditor.tsx
//
// Plan-v3 core Slice E1 (edm_content): build an email from blocks. The block
// list is saved as-is; the server compiles it to 600 px table HTML through
// the sanitiser (Slice D1) and re-checks every rule below. There is no
// preview here: the server-rendered PreviewPane is the only preview.

import React from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { EDM_UI, mergeLintError } from '../../../../utils/edm';
import {
  EDM_BLOCK_LABEL,
  EDM_BLOCK_TYPES,
  EDM_MAX_BLOCKS,
  EDM_MAX_IMAGE_WIDTH,
  newEdmBlock,
} from '../../../../hooks/useEdmContent';
import type { EdmBlock, EdmBlockType } from '../../../../hooks/useEdmContent';

const HTTPS_URL = /^https:\/\/[^\s"'<>{}\\]+$/;

/** The first problem with one block, or null (client lint; the server is the authority). */
export function blockLint(b: EdmBlock): string | null {
  const text = (v: string, what: string) => (v.trim() ? mergeLintError(v) : `Add the ${what}.`);
  switch (b.type) {
    case 'heading':
      return text(b.text, 'heading text');
    case 'text':
      return text(b.text, 'text');
    case 'button':
      if (!b.label.trim()) return 'Add the button label.';
      if (!HTTPS_URL.test(b.url.trim())) return 'The button link must be a full https:// address.';
      return mergeLintError(b.label);
    case 'image':
      if (!HTTPS_URL.test(b.url.trim())) return 'The image address must be a full https:// address.';
      if (!b.alt.trim()) return 'Add alt text describing the image.';
      if (b.width != null && !(Number.isInteger(b.width) && b.width >= 1 && b.width <= EDM_MAX_IMAGE_WIDTH)) {
        return `Width must be a whole number from 1 to ${EDM_MAX_IMAGE_WIDTH}.`;
      }
      if (b.linkUrl && !HTTPS_URL.test(b.linkUrl.trim())) return 'The image link must be a full https:// address.';
      return mergeLintError(b.alt);
    case 'twoColumn':
      if (!b.left.trim() && !b.right.trim()) return 'Add text to at least one column.';
      return mergeLintError(b.left) ?? mergeLintError(b.right);
    case 'divider':
    case 'spacer':
      return null;
    default:
      return 'Unknown block.';
  }
}

/** Lint for the whole list, or null. Blocks Save, Preview and Freeze like the other paths. */
export function blocksLint(blocks: readonly EdmBlock[]): string | null {
  // An empty list would save as Blocks with nothing to compile and reopen as pasted HTML.
  if (blocks.length === 0) return 'Add at least one block.';
  if (blocks.length > EDM_MAX_BLOCKS) return `An email can have up to ${EDM_MAX_BLOCKS} blocks.`;
  for (let i = 0; i < blocks.length; i += 1) {
    if (blockLint(blocks[i]) !== null) return `Fix block ${i + 1} first.`;
  }
  return null;
}

const BlockEditor: React.FC<{
  blocks: EdmBlock[];
  onChange: (blocks: EdmBlock[]) => void;
  disabled?: boolean;
}> = ({ blocks, onChange, disabled }) => {
  const atCap = blocks.length >= EDM_MAX_BLOCKS;
  const set = (i: number, b: EdmBlock) => onChange(blocks.map((x, j) => (j === i ? b : x)));
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= blocks.length) return;
    const next = blocks.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = (type: EdmBlockType) => {
    if (!atCap) onChange([...blocks, newEdmBlock(type)]);
  };

  return (
    <div className="space-y-4" data-testid="edm-block-editor">
      {blocks.length === 0 && (
        <p className="text-sm text-gray-400">Add blocks below to build your email. Start with a heading and some text.</p>
      )}
      {blocks.map((b, i) => {
        const lint = blockLint(b);
        const id = `edm-block-${i}`;
        return (
          // Blocks have no stable id; the index is the identity within one edit.
          <div key={i} className="space-y-2 rounded-lg border border-gray-700 bg-gray-900/40 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                {i + 1}. {EDM_BLOCK_LABEL[b.type]}
              </span>
              {!disabled && (
                <div className="flex items-center gap-1">
                  <button type="button" className="p-1 text-gray-400 hover:text-gray-100 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move block ${i + 1} up`}>
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button type="button" className="p-1 text-gray-400 hover:text-gray-100 disabled:opacity-30" disabled={i === blocks.length - 1} onClick={() => move(i, 1)} aria-label={`Move block ${i + 1} down`}>
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button type="button" className="p-1 text-gray-400 hover:text-red-300" onClick={() => remove(i)} aria-label={`Remove block ${i + 1}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            {(b.type === 'heading' || b.type === 'text') && (
              <div>
                <label htmlFor={`${id}-text`} className="sr-only">
                  {EDM_BLOCK_LABEL[b.type]} {i + 1}
                </label>
                {b.type === 'heading' ? (
                  <input id={`${id}-text`} className={EDM_UI.input} maxLength={200} value={b.text} disabled={disabled} onChange={(e) => set(i, { ...b, text: e.target.value })} />
                ) : (
                  <textarea id={`${id}-text`} className={EDM_UI.input} rows={4} maxLength={5000} value={b.text} disabled={disabled} onChange={(e) => set(i, { ...b, text: e.target.value })} />
                )}
              </div>
            )}
            {b.type === 'button' && (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div>
                  <label htmlFor={`${id}-label`} className={EDM_UI.label}>Button label</label>
                  <input id={`${id}-label`} className={EDM_UI.input} maxLength={80} value={b.label} disabled={disabled} onChange={(e) => set(i, { ...b, label: e.target.value })} />
                </div>
                <div>
                  <label htmlFor={`${id}-url`} className={EDM_UI.label}>Button link (https://)</label>
                  <input id={`${id}-url`} className={EDM_UI.input} maxLength={2000} value={b.url} disabled={disabled} onChange={(e) => set(i, { ...b, url: e.target.value })} />
                </div>
              </div>
            )}
            {b.type === 'image' && (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label htmlFor={`${id}-src`} className={EDM_UI.label}>Image address (https://)</label>
                  <input id={`${id}-src`} className={EDM_UI.input} maxLength={2000} value={b.url} disabled={disabled} onChange={(e) => set(i, { ...b, url: e.target.value })} />
                </div>
                <div>
                  <label htmlFor={`${id}-alt`} className={EDM_UI.label}>Alt text</label>
                  <input id={`${id}-alt`} className={EDM_UI.input} maxLength={200} value={b.alt} disabled={disabled} onChange={(e) => set(i, { ...b, alt: e.target.value })} />
                </div>
                <div>
                  <label htmlFor={`${id}-width`} className={EDM_UI.label}>Width (up to {EDM_MAX_IMAGE_WIDTH})</label>
                  <input
                    id={`${id}-width`}
                    type="number"
                    min={1}
                    max={EDM_MAX_IMAGE_WIDTH}
                    className={EDM_UI.input}
                    value={b.width ?? ''}
                    disabled={disabled}
                    onChange={(e) => set(i, { ...b, width: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor={`${id}-link`} className={EDM_UI.label}>Link when clicked (optional, https://)</label>
                  <input id={`${id}-link`} className={EDM_UI.input} maxLength={2000} value={b.linkUrl ?? ''} disabled={disabled} onChange={(e) => set(i, { ...b, linkUrl: e.target.value || null })} />
                </div>
              </div>
            )}
            {b.type === 'spacer' && (
              <div>
                <label htmlFor={`${id}-height`} className={EDM_UI.label}>Height</label>
                <select
                  id={`${id}-height`}
                  className={EDM_UI.input}
                  value={String(b.height ?? 24)}
                  disabled={disabled}
                  onChange={(e) => set(i, { ...b, height: Number(e.target.value) })}
                >
                  <option value="12">Small</option>
                  <option value="24">Medium</option>
                  <option value="48">Large</option>
                </select>
              </div>
            )}
            {b.type === 'twoColumn' && (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div>
                  <label htmlFor={`${id}-left`} className={EDM_UI.label}>Left column</label>
                  <textarea id={`${id}-left`} className={EDM_UI.input} rows={3} maxLength={2000} value={b.left} disabled={disabled} onChange={(e) => set(i, { ...b, left: e.target.value })} />
                </div>
                <div>
                  <label htmlFor={`${id}-right`} className={EDM_UI.label}>Right column</label>
                  <textarea id={`${id}-right`} className={EDM_UI.input} rows={3} maxLength={2000} value={b.right} disabled={disabled} onChange={(e) => set(i, { ...b, right: e.target.value })} />
                </div>
              </div>
            )}
            {lint && !disabled && <p className="text-xs text-red-400">{lint}</p>}
          </div>
        );
      })}
      {!disabled && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Add a block">
            {EDM_BLOCK_TYPES.map((t) => (
              <button key={t} type="button" className={EDM_UI.secondaryButton} disabled={atCap} onClick={() => add(t)}>
                <Plus className="h-3.5 w-3.5" /> {EDM_BLOCK_LABEL[t]}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            {blocks.length}/{EDM_MAX_BLOCKS} blocks. Your logo, brand colour, unsubscribe link and legal footer are added
            automatically. Save and refresh the preview to see the finished email.
          </p>
        </div>
      )}
    </div>
  );
};

export default BlockEditor;
